package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.data.AirlineDatabase
import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.state.AppSettings
import com.banyan.flightviewer.state.Bookmark
import com.banyan.flightviewer.state.EnrichedFlightData
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.dom.removeAllChildren
import com.banyan.flightviewer.web.dom.span
import com.banyan.flightviewer.web.dom.text
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.Element

/**
 * Selected-flight detail panel. Mirrors the surface of the desktop
 * `DetailPanel.kt` (header, photo, route block, data grid, aircraft info)
 * with web-friendly layout collapses on narrow viewports.
 */
fun mountDetailPanel(parent: Element, app: WebAppContainer) {
    val container = parent.div(className = "fv-detail")
    container.asDynamic().style.display = "none"

    fun render() {
        container.removeAllChildren()
        val hex = app.appState.selectedCallsign.value ?: run {
            container.asDynamic().style.display = "none"
            return
        }
        val ac = app.appState.aircraftList.value.find { it.hex == hex } ?: run {
            container.asDynamic().style.display = "none"
            return
        }
        val enriched = app.appState.cachedEnrichedData.value[hex]
        val merged = mergeEnrichment(ac, enriched)
        val photoBytes = app.appState.selectedAircraftPhoto.value
        val isBookmarked = app.appState.bookmarks.value.any { it.hex == hex }
        val s = app.appState.settings.value
        renderInto(container, app, merged, enriched, photoBytes, isBookmarked, s)
        container.asDynamic().style.display = "block"
    }

    app.appState.scope.bindFlow(app.appState.selectedCallsign) { render() }
    app.appState.scope.bindFlow(app.appState.aircraftList) { render() }
    app.appState.scope.bindFlow(app.appState.cachedEnrichedData) { render() }
    app.appState.scope.bindFlow(app.appState.selectedAircraftPhoto) { render() }
    app.appState.scope.bindFlow(app.appState.bookmarks) { render() }
}

private fun mergeEnrichment(ac: AircraftData, e: EnrichedFlightData?): AircraftData {
    if (e == null) return ac
    return ac.copy(
        origin = e.origin.takeIf { it.isNotBlank() && it != "_none" } ?: ac.origin,
        destination = e.destination.ifEmpty { ac.destination },
        aircraftType = e.aircraftType.ifEmpty { ac.aircraftType },
        registration = e.registration.ifEmpty { ac.registration },
    )
}

private fun renderInto(
    parent: Element,
    app: WebAppContainer,
    ac: AircraftData,
    enriched: EnrichedFlightData?,
    photoBytes: ByteArray?,
    isBookmarked: Boolean,
    settings: AppSettings,
) {
    val airlineName = AirlineDatabase.getAirlineName(ac.callsign)
    val regDisplay = ac.registration.takeIf { it.isNotBlank() }?.uppercase()
    val typeDisplay = ac.aircraftType.takeIf { it.isNotBlank() && it != "Unknown" }
    val originCode = ac.origin.cleanCode()
    val destCode = ac.destination.cleanCode()
    val depFormatted = enriched?.departureTime?.let { formatTime(it) } ?: ""
    val etaFormatted = enriched?.eta?.let { formatTime(it) } ?: ""
    val flightTime = enriched?.departureTime?.let { computeFlightTime(it) } ?: ""
    val timeRemaining = computeTimeRemaining(enriched?.eta)
    val flightProgress = computeFlightProgress(enriched?.departureTime, enriched?.eta)
    val distDisplay = if (settings.distanceUnits == "miles") {
        "${formatOneDecimal(ac.distanceMiles)} mi"
    } else {
        "${formatOneDecimal(ac.distance)} km"
    }

    // Header
    parent.div(className = "fv-detail__header") {
        span(className = "fv-detail__callsign") { textContent = ac.callsign }
        if (typeDisplay != null) {
            span(className = "fv-detail__badge") { textContent = typeDisplay }
        }
        if (regDisplay != null) {
            span(className = "fv-detail__badge") { textContent = regDisplay }
        }
        div(className = "fv-detail__spacer") { }
        // Recenter-on-flight button (↗ arrow-up-right, reads as
        // "jump to the thing"). Fires a map-focus request to the
        // aircraft's current live position; panMapTo in MapView
        // handles the mobile bottom-sheet offset so the marker lands
        // above the sheet rather than hidden under it.
        val recenterBtn = button(className = "fv-detail__icon-btn") {
            textContent = "\u2197"
            title = "Recenter map on this flight"
        }
        recenterBtn.addEventListener("click", { _: org.w3c.dom.events.Event ->
            app.uiState.requestMapFocus(ac.lat, ac.lon)
        })
        // Share button: generates a deep-link URL for the current flight
        // and offers it via the Web Share API (native sheet on mobile,
        // browser share-target on supporting desktop browsers). Falls
        // back to clipboard copy on browsers without `navigator.share`,
        // with a transient status-bar confirmation either way. Uses the
        // iOS-style share glyph (box + up-arrow) rendered inline via
        // SVG for crispness at any DPI.
        val shareBtn = button(className = "fv-detail__icon-btn") {
            title = "Share this flight"
        }
        shareBtn.innerHTML = SHARE_ICON_SVG
        shareBtn.addEventListener("click", { _: org.w3c.dom.events.Event ->
            shareFlight(app, ac.hex, ac.callsign)
        })

        button(className = if (isBookmarked) "fv-detail__icon-btn active" else "fv-detail__icon-btn") {
            textContent = if (isBookmarked) "\u2605" else "\u2606"
            title = "Bookmark"
            onClick {
                val bms = app.appState.bookmarks.value
                if (isBookmarked) {
                    app.appState.removeBookmark(ac.hex)
                } else {
                    app.appState.addBookmark(
                        Bookmark(
                            hex = ac.hex,
                            callsign = ac.callsign,
                            registration = ac.registration,
                            aircraftType = ac.aircraftType,
                            airline = airlineName ?: "",
                            origin = ac.origin,
                            destination = ac.destination,
                        ),
                    )
                }
                app.settingsManager.saveBookmarks(app.appState.bookmarks.value)
            }
        }
        button(className = "fv-detail__icon-btn") {
            textContent = "\u2715"
            title = "Close"
            onClick { app.appState.selectFlight(null) }
        }
    }

    if (airlineName != null) {
        parent.div(className = "fv-detail__airline") { textContent = airlineName }
    }

    // Photo
    if (photoBytes != null) {
        val img = document.createElement("img") as HTMLImageElement
        img.className = "fv-detail__photo"
        img.alt = "${ac.callsign} ${ac.aircraftType}"
        img.src = bytesToDataUrl(photoBytes)
        parent.appendChild(img)
    } else {
        // Kick off a fetch via the server proxy. We do this here so the panel
        // doesn't need to know about FetchPhoto manager wiring.
        app.appState.scope.launch {
            val fresh = app.serverClient.fetchPhotoBytes(ac.hex, ac.registration.ifBlank { null })
            if (fresh != null && app.appState.selectedCallsign.value == ac.hex) {
                app.appState.setSelectedAircraftPhoto(fresh)
            }
        }
    }

    // Route block
    if (originCode != null || destCode != null) {
        parent.div(className = "fv-detail__route") {
            div(className = "fv-detail__rcol") {
                div(className = "fv-detail__rcode") { textContent = originCode ?: "\u2014" }
                airportName(this, originCode)
                timeLabel(this, "DEP", depFormatted, dot = false)
            }
            div(className = "fv-detail__rsep") {
                span { textContent = "\u2708" }
                if (flightProgress > 0.0) {
                    div(className = "fv-detail__progress") {
                        val bar = div(className = "fv-detail__progress-bar")
                        bar.asDynamic().style.width = "${(flightProgress * 100).toInt()}%"
                    }
                }
            }
            div(className = "fv-detail__rcol") {
                div(className = "fv-detail__rcode") { textContent = destCode ?: "\u2014" }
                airportName(this, destCode)
                timeLabel(this, "ETA", etaFormatted, dot = etaFormatted.isNotBlank())
            }
        }

        if (flightTime.isNotBlank() || timeRemaining.isNotBlank()) {
            parent.div(className = "fv-detail__rsummary") {
                if (flightTime.isNotBlank()) text("$flightTime elapsed")
                if (flightTime.isNotBlank() && timeRemaining.isNotBlank()) text(" \u00B7 ")
                if (timeRemaining.isNotBlank()) text("$timeRemaining remaining")
            }
        }
        parent.div(className = "fv-detail__divider") { }
    }

    // Data grid: 6 cells
    parent.div(className = "fv-detail__grid") {
        cell(this, "ALTITUDE", formatAltitude(ac.altitude, settings.altitudeUnits))
        cell(this, "SPEED", formatSpeed(ac.speed, settings.speedUnits))
        cell(this, "HEADING", "${ac.heading.toInt()}\u00B0")
        cell(this, "VERT RATE", formatVerticalRate(ac.verticalRate), vrClass(ac.verticalRate))
        cell(this, "SQUAWK", formatSquawk(ac.squawk), squawkClass(ac.squawk))
        cell(this, "DISTANCE", distDisplay)
    }

    parent.div(className = "fv-detail__divider") { }

    parent.div(className = "fv-detail__grid") {
        cell(this, "HEX", ac.hex.uppercase())
        cell(this, "REG", regDisplay ?: "\u2014")
        cell(this, "TYPE", typeDisplay ?: "\u2014")
    }
}

private fun cell(parent: Element, label: String, value: String, valueClass: String = "") {
    parent.div {
        div(className = "fv-detail__cell-label") { textContent = label }
        div(className = "fv-detail__cell-value $valueClass".trim()) { textContent = value }
    }
}

private fun airportName(parent: Element, code: String?) {
    if (code == null) return
    val name = AirportDatabase.getName(code)
    if (name == code) return
    parent.div(className = "fv-detail__rname") { textContent = name.uppercase() }
}

private fun timeLabel(parent: Element, label: String, value: String, dot: Boolean) {
    parent.div(className = "fv-detail__rtime-label") { textContent = label }
    if (value.isBlank()) {
        parent.div(className = "fv-detail__rtime") { textContent = "\u2014" }
    } else {
        parent.div(className = "fv-detail__rtime") {
            if (dot) span { textContent = "\u2022 " }
            text(value)
        }
    }
}

private fun String.cleanCode(): String? =
    takeIf { isNotBlank() && !equals("null", ignoreCase = true) }

private fun bytesToDataUrl(bytes: ByteArray): String {
    // base64-encode the bytes for an inline data URL. ByteArray on Kotlin/JS
    // is a Int8Array under the hood; convert via String.fromCharCode for btoa.
    val sb = StringBuilder(bytes.size)
    for (b in bytes) sb.append((b.toInt() and 0xff).toChar())
    val b64 = js("btoa")(sb.toString()) as String
    return "data:image/jpeg;base64,$b64"
}

private fun formatTime(iso: String): String {
    if (iso.isBlank() || iso.equals("null", true)) return ""
    val instant = parseInstantOrNull(iso) ?: return ""
    val ms = instant.toEpochMilliseconds()
    val d = kotlin.js.Date(ms)
    val hours = d.getHours()
    val minutes = d.getMinutes()
    val ampm = if (hours >= 12) "PM" else "AM"
    val h12 = ((hours + 11) % 12) + 1
    val mm = minutes.toString().padStart(2, '0')
    return "$h12:$mm $ampm"
}

private fun computeFlightTime(dep: String): String {
    if (dep.isBlank() || dep.equals("null", true)) return ""
    val instant = parseInstantOrNull(dep) ?: return ""
    val elapsedMs = Clock.System.now().toEpochMilliseconds() - instant.toEpochMilliseconds()
    if (elapsedMs < 60_000) return ""
    return formatHm(elapsedMs)
}

private fun computeTimeRemaining(eta: String?): String {
    if (eta.isNullOrBlank() || eta.equals("null", true)) return ""
    val instant = parseInstantOrNull(eta) ?: return ""
    val remainingMs = instant.toEpochMilliseconds() - Clock.System.now().toEpochMilliseconds()
    if (remainingMs < 60_000) return ""
    return formatHm(remainingMs)
}

private fun computeFlightProgress(dep: String?, eta: String?): Double {
    if (dep.isNullOrBlank() || eta.isNullOrBlank()) return 0.0
    val d = parseInstantOrNull(dep) ?: return 0.0
    val e = parseInstantOrNull(eta) ?: return 0.0
    val total = (e.toEpochMilliseconds() - d.toEpochMilliseconds()).toDouble()
    if (total <= 0) return 0.0
    val elapsed = (Clock.System.now().toEpochMilliseconds() - d.toEpochMilliseconds()).toDouble()
    return (elapsed / total).coerceIn(0.0, 1.0)
}

private fun parseInstantOrNull(s: String): Instant? {
    if (s.equals("null", true)) return null
    return try {
        Instant.parse(s)
    } catch (_: Throwable) {
        try {
            Instant.fromEpochSeconds(s.toLong())
        } catch (_: Throwable) {
            null
        }
    }
}

private fun formatHm(ms: Long): String {
    val totalMin = (ms / 60_000).toInt()
    val h = totalMin / 60
    val m = totalMin % 60
    val mm = m.toString().padStart(2, '0')
    return if (h > 0) "${h}h ${mm}m" else "${m}m"
}

private fun formatAltitude(altitude: String, units: String): String {
    val alt = altitude.toDoubleOrNull() ?: return "$altitude $units"
    return if (units == "m") "${(alt * 0.3048).toInt().withCommas()} m"
    else "${alt.toInt().withCommas()} ft"
}

private fun formatSpeed(speed: Double, units: String): String = when (units) {
    "kmh", "km/h" -> "${(speed * 1.852).toInt()} km/h"
    "mph" -> "${(speed * 1.15078).toInt()} mph"
    else -> "${speed.toInt()} kts"
}

private fun formatVerticalRate(vr: Int): String = when {
    vr > 0 -> "\u2191 ${vr.withCommas()} fpm"
    vr < 0 -> "\u2193 ${(-vr).withCommas()} fpm"
    else -> "0 fpm"
}

private fun vrClass(vr: Int): String = when {
    vr > 0 -> "up"
    vr < 0 -> "down"
    else -> ""
}

private fun formatSquawk(squawk: String): String {
    if (squawk.isBlank()) return "\u2014"
    return when (squawk) {
        "7700" -> "$squawk EMER"
        "7600" -> "$squawk NORAD"
        "7500" -> "$squawk HIJACK"
        else -> squawk
    }
}

private fun squawkClass(squawk: String): String =
    if (squawk == "7700" || squawk == "7600" || squawk == "7500") "emer" else ""

private fun formatOneDecimal(v: Double): String {
    val rounded = (v * 10).toInt() / 10.0
    return rounded.toString()
}

private fun Int.withCommas(): String {
    val s = this.toString()
    val sb = StringBuilder()
    for ((i, c) in s.withIndex()) {
        if (i > 0 && (s.length - i) % 3 == 0) sb.append(',')
        sb.append(c)
    }
    return sb.toString()
}

/**
 * Builds the flight deep-link URL and hands off to the shared [shareUrl]
 * helper (Web Share API → clipboard → legacy copy fallback chain).
 *
 * URL shape: `https://<host>/flight/<hex>` — handled by the SPA catchall
 * in the server's StaticRoutes + the DeepLink loader on the client.
 */
private fun shareFlight(
    app: com.banyan.flightviewer.web.state.WebAppContainer,
    hex: String,
    callsign: String,
) {
    val host = kotlinx.browser.window.location.origin
    val url = "$host/flight/${hex.lowercase()}"
    shareUrl(
        app = app,
        url = url,
        title = "$callsign on FlightView",
        text = "Live tracking for flight $callsign",
    )
}
